import { Fetch } from "@syncfusion/ej2-base";
import {
  CrudOptions,
  DataOptions,
  DataResult,
  Query,
  ODataV4Adaptor,
  DataManager,
  Predicate,
} from "@syncfusion/ej2-data";
import {
  ODataV4QueryStringAndMaybeHeadersFactory,
  ODataV4TransformDataMethod,
} from "./types";

export interface BaseODataV4AdaptorOptions<TData> {
  beforeSendFn?: ODataV4QueryStringAndMaybeHeadersFactory;
  transformData?: ODataV4TransformDataMethod<TData>;
  removeLeadingQuestionMark?: boolean;
  replaceUndefinedInQueryStringWithNulls?: boolean;
  removeUnsupportedParameters?: boolean;
}

// The generic type parameter is aspirational.
export class BaseODataV4Adaptor<TData = any> extends ODataV4Adaptor {
  protected beforeSendFn?: ODataV4QueryStringAndMaybeHeadersFactory;
  protected transformData?: ODataV4TransformDataMethod<TData>;
  protected removeLeadingQuestionMark: boolean = true;
  protected replaceUndefinedInQueryStringWithNulls: boolean = true;
  protected removeUnsupportedParameters: boolean = true;
  private STRING_TO_USE_TO_BYPASS_GUID_CHECK = "replace-me-with-value-thanks";

  constructor(options: BaseODataV4AdaptorOptions<TData>) {
    super();
    this.beforeSendFn = options.beforeSendFn;
    this.transformData = options.transformData;
    this.removeLeadingQuestionMark = options.removeLeadingQuestionMark ?? false;
    this.replaceUndefinedInQueryStringWithNulls =
      options.replaceUndefinedInQueryStringWithNulls ?? true;
    this.removeUnsupportedParameters =
      options.removeUnsupportedParameters ?? true;
  }

  beforeSend = async (
    dataManager: DataManager,
    request: Request,
    ajax: Fetch
  ) => {
    /** Fetch Query */
    const url = new URL(ajax.url);
    let queryString = url.search;
    let extraHeaders = undefined; // Typically used for authorization headers

    /**
     * Cleanups
     * Replaces any undefined keyword for null to supprto api types
     */
    if (this.replaceUndefinedInQueryStringWithNulls) {
      queryString = queryString.replace(/undefined/g, "null");
    }

    /** Send onBeforeSend event */
    if (!!this.beforeSendFn) {
      const [qry, headers] = this.beforeSendFn({
        dataManager,
        request,
        ajax,
        queryString,
      });

      queryString = qry;
      extraHeaders = headers;
    }

    /**
     * Send request on Body
     *
     * New Note
     * The only purpose of the question mark is to haphazardly exclude the first parameter from being parsed.
     * Instead of the question mark, we can just get rid of the unsupported parameters. This should be the preferred method.
     *
     * Previous Note (wrong)
     * Note that is important that the body still sends the ? on the beginning for full support of queriers, including top.
     */

    const queryStringHadQuestionMark = queryString.startsWith("?") === true;

    const queryStringWithoutQuestionMark = queryString.startsWith("?")
      ? queryString.substring(1)
      : queryString;

    const queryStringParts = queryStringWithoutQuestionMark.split("&");

    const finalQueryStringParts = [];
    for (const part of queryStringParts) {
      if (
        this.removeUnsupportedParameters &&
        (part.startsWith("$apply=") || part.startsWith("$select="))
      ) {
        continue;
      }
      finalQueryStringParts.push(part);
    }

    queryString = finalQueryStringParts.join("&");

    if (!this.removeLeadingQuestionMark && queryStringHadQuestionMark) {
      queryString = "?" + queryString;
    }

    ajax.data = queryString;

    request = new Request(`${url.origin}${url.pathname}$query`, {
      method: "POST",
      headers: {
        ...request.headers,
        ...extraHeaders,
      },
      body: queryString,
    });

    ajax.fetchRequest = request;
  };

  onPredicate(
    predicate: Predicate,
    query: Query | boolean,
    requiresCast?: boolean
  ): string {
    // SyncFusion tries to be smart and remove quotes if it think it is a GUID.
    // To trick it, we can put quotes around it to indicate that it is a string.

    const value = predicate.value;

    const forceString =
      value &&
      typeof value === "string" &&
      value.length > 1 &&
      value.startsWith("'") &&
      value.endsWith("'");

    if (forceString) {
      predicate.value = this.STRING_TO_USE_TO_BYPASS_GUID_CHECK;
    }

    let result = super.onPredicate(predicate, query, requiresCast);

    if (
      forceString &&
      result &&
      typeof result === "string" &&
      result.includes(this.STRING_TO_USE_TO_BYPASS_GUID_CHECK)
    ) {
      result = result.replace(
        `'${this.STRING_TO_USE_TO_BYPASS_GUID_CHECK}'`,
        value
      );
    }

    return result;
  }

  processResponse(
    data: DataResult,
    ds?: DataOptions,
    query?: Query,
    xhr?: Request,
    request?: Fetch,
    changes?: CrudOptions
  ): Object {
    const processResult = super.processResponse.apply(this, [
      data,
      ds,
      query,
      xhr,
      request,
      changes,
    ]);

    if (Array.isArray(processResult)) {
      return this.transformData?.(processResult) ?? processResult;
    }

    return processResult;
  }
}

export default BaseODataV4Adaptor;
